import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2 } from 'lucide-react';
import Modal from '../ui/Modal';

interface ReportCustomerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (reason: string) => void;
    clientName?: string;
    isLoading?: boolean;
}

const ReportCustomerModal: React.FC<ReportCustomerModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    clientName,
    isLoading = false
}) => {
    const { t } = useTranslation();
    const [selectedReason, setSelectedReason] = useState<string>('no_show');
    const [otherReason, setOtherReason] = useState<string>('');

    const reasons = [
        { id: 'no_show', label: t('bookings.report.reason_no_show', 'Non si è presentato senza avvisare') },
        { id: 'late_cancel', label: t('bookings.report.reason_late_cancel', 'Ha disdetto all\'ultimo minuto') },
        { id: 'other', label: t('bookings.report.reason_other', 'Altro') },
    ];

    const handleConfirm = () => {
        const finalReason = selectedReason === 'other' ? otherReason : reasons.find(r => r.id === selectedReason)?.label || '';
        onConfirm(finalReason);
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            className="max-w-md"
            headerClassName="bg-white border-b border-gray-100 pb-4"
            headerContent={
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="bg-red-50 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider border border-red-100 flex items-center gap-1">
                            <AlertTriangle size={12} />
                            {t('bookings.report.badge', 'Segnalazione')}
                        </span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 leading-tight">
                        {t('bookings.report.title', 'Segnala Cliente')}
                    </h2>
                </div>
            }
        >
            <div className="space-y-6">
                <div>
                    <p className="text-gray-600 text-sm">
                        {t('bookings.report.description', 'Stai per segnalare il cliente')} <span className="font-bold text-gray-900">{clientName}</span>. {t('bookings.report.description_sub', 'Scegli il motivo della segnalazione di seguito:')}
                    </p>
                </div>

                <div className="space-y-3">
                    {reasons.map((reason) => (
                        <label
                            key={reason.id}
                            className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${selectedReason === reason.id ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-red-200 bg-white'}`}
                        >
                            <input
                                type="radio"
                                name="reportReason"
                                value={reason.id}
                                checked={selectedReason === reason.id}
                                onChange={() => setSelectedReason(reason.id)}
                                className="hidden"
                            />
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${selectedReason === reason.id ? 'border-red-500' : 'border-gray-300'}`}>
                                {selectedReason === reason.id && (
                                    <div className="w-2.5 h-2.5 bg-red-500 rounded-full" />
                                )}
                            </div>
                            <span className={`font-medium ${selectedReason === reason.id ? 'text-red-900' : 'text-gray-700'}`}>
                                {reason.label}
                            </span>
                        </label>
                    ))}
                </div>

                {selectedReason === 'other' && (
                    <div className="space-y-2 animate-in fade-in slide-in-from-top-2">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                            {t('bookings.report.other_reason_label', 'Specifica il motivo')}
                        </label>
                        <textarea
                            value={otherReason}
                            onChange={(e) => setOtherReason(e.target.value)}
                            placeholder={t('bookings.report.other_reason_placeholder', 'Inserisci una motivazione...')}
                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-200 outline-none font-medium resize-none min-h-[100px]"
                        />
                    </div>
                )}

                <div className="pt-6 border-t border-gray-100 flex gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="flex-1 py-3.5 rounded-xl font-bold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-70"
                    >
                        {t('common.cancel', 'Annulla')}
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={isLoading || (selectedReason === 'other' && !otherReason.trim())}
                        className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-bold bg-red-500 hover:bg-red-600 shadow-lg shadow-red-200 transition-all disabled:opacity-70 disabled:shadow-none"
                    >
                        {isLoading && <Loader2 size={18} className="animate-spin" />}
                        {t('bookings.report.confirm_btn', 'Segnala')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ReportCustomerModal;
